import React, { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ChevronDown, Phone, ShieldCheck } from 'lucide-react'
import SEO from '@components/SEO'
import { useFirebaseAuth } from '@context/FirebaseAuthContext'
import { sendCertificationOtp, clearPhoneRecaptcha } from '@lib/certificationPhoneAuth'
import { CERTIFICATION_BTN_OUTLINE_DARK } from '@lib/certificationUi'
import { parseSafeCertificationNext } from '@lib/certificationRouting'
import {
  buildE164FromDialAndNational,
  CERTIFICATION_PHONE_DIAL_CODES,
  DEFAULT_CERTIFICATION_DIAL,
} from '@lib/certificationPhoneDialCodes'

function nationalPlaceholder(dial) {
  if (dial === '+91') return '98765 43210'
  if (dial === '+1') return '555 123 4567'
  if (dial === '+44') return '7700 900123'
  return 'Enter your number'
}

/** Narrow country dial — smaller type and height than the national number field. */
const CERT_TEL_DIAL =
  'h-10 min-h-10 max-h-10 w-full box-border rounded-md border border-neutral-600 bg-navy-900/80 text-xs text-white shadow-none outline-none transition-[border-color,box-shadow] focus:border-primary-500/45 focus:ring-2 focus:ring-primary-500/25 focus:ring-offset-0 focus:ring-offset-transparent'

/** Primary national number — larger tap target and typography. */
const CERT_TEL_PHONE =
  'h-14 min-h-[3.5rem] max-h-14 w-full box-border rounded-md border border-neutral-600 bg-navy-900/80 text-base sm:text-lg text-white shadow-none outline-none transition-[border-color,box-shadow] focus:border-primary-500/45 focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-0 focus:ring-offset-transparent'

export default function CertificationSignInPage() {
  const { user, loading: authLoading } = useFirebaseAuth()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const nextPath = parseSafeCertificationNext(searchParams.get('next'))

  const [step, setStep] = useState('phone')
  const [dialCode, setDialCode] = useState(DEFAULT_CERTIFICATION_DIAL)
  const [nationalPhone, setNationalPhone] = useState('')
  const [code, setCode] = useState('')
  const [confirmation, setConfirmation] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    return () => clearPhoneRecaptcha()
  }, [])

  useEffect(() => {
    if (!authLoading && user) {
      navigate(nextPath, { replace: true })
    }
  }, [authLoading, user, navigate, nextPath])

  const handleSendCode = async (e) => {
    e.preventDefault()
    setError('')
    const phone = buildE164FromDialAndNational(dialCode, nationalPhone)
    if (!phone) {
      setError(
        'Choose your country code and enter a valid mobile number (10–15 digits total including country code). Do not include the country code again in the number field.'
      )
      return
    }
    setBusy(true)
    try {
      const conf = await sendCertificationOtp(phone, 'trp-phone-recaptcha')
      setConfirmation(conf)
      setStep('code')
    } catch (err) {
      console.error(err)
      const code = err?.code || ''
      if (code === 'auth/invalid-phone-number') {
        setError('That phone number does not look valid. Check the country code and try again.')
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please wait a little while before requesting another code.')
      } else {
        setError('We could not send the SMS. Confirm Phone sign-in and reCAPTCHA are enabled in Firebase, and that this domain is authorised.')
      }
    } finally {
      setBusy(false)
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    if (!confirmation) return
    setError('')
    setBusy(true)
    try {
      await confirmation.confirm(code.replace(/\D/g, ''))
      navigate(nextPath, { replace: true })
    } catch (err) {
      console.error(err)
      setError('That code did not work. Check the SMS and try again, or request a new code.')
    } finally {
      setBusy(false)
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-neutral-400 text-sm">Checking your session…</p>
      </div>
    )
  }

  if (user) {
    return <Navigate to={nextPath} replace />
  }

  return (
    <div className="bg-black min-h-screen pb-24">
      <SEO
        title="Sign in — Total Rewards certification"
        description="Sign in with your mobile number to take a certification practice assessment, save results, and download certificates."
      />
      <section className="pt-14 lg:pt-16 pb-12 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 to-black opacity-90" />
        <div className="w-full px-6 sm:px-8 lg:px-12 relative max-w-lg mx-auto">
          <Link
            to="/certification"
            className="inline-flex items-center text-sm text-primary-400 hover:text-primary-300 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Certification home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-effect-dark rounded-2xl border border-primary-500/25 p-8 sm:p-10 space-y-6"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-primary-500/15 p-3 border border-primary-500/30">
                <ShieldCheck className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <h1 className="text-display-3 text-white mb-2">Sign in</h1>
                <p className="text-sm text-neutral-400 leading-relaxed">
                  Enter your mobile number and we will text you a one-time code — no password to remember. After signing
                  in you can start an assessment, review past scores, and re-download certificates. Use the same number
                  whenever you return so your progress stays linked to you.
                </p>
              </div>
            </div>

            {error ? (
              <div className="rounded-lg border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-200">
                {error}
              </div>
            ) : null}

            {step === 'phone' ? (
              <form onSubmit={handleSendCode} className="space-y-5">
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-neutral-300">Mobile number</label>
                  <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-[minmax(0,6rem)_minmax(0,1fr)] sm:items-center">
                    <div className="relative min-w-0 w-full max-w-[10rem] sm:max-w-[6.25rem] sm:justify-self-start">
                      <label htmlFor="phone-dial" className="sr-only">
                        Country code
                      </label>
                      <select
                        id="phone-dial"
                        value={dialCode}
                        onChange={(e) => setDialCode(e.target.value)}
                        className={`${CERT_TEL_DIAL} cursor-pointer appearance-none truncate pl-2 pr-7 py-0 leading-10`}
                      >
                        {CERTIFICATION_PHONE_DIAL_CODES.map((o) => (
                          <option key={o.dial} value={o.dial} title={o.label}>
                            {o.compact}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        className="pointer-events-none absolute right-1.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500"
                        aria-hidden
                      />
                    </div>
                    <div className="relative min-w-0">
                      <label htmlFor="phone-national" className="sr-only">
                        Mobile number without country code
                      </label>
                      <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-500" />
                      <input
                        id="phone-national"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder={nationalPlaceholder(dialCode)}
                        value={nationalPhone}
                        onChange={(e) => setNationalPhone(e.target.value)}
                        className={`${CERT_TEL_PHONE} placeholder:text-neutral-500 pl-12 pr-4 leading-[3.5rem]`}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-neutral-500 leading-relaxed">
                    Pick your country from the compact list (hover an option on desktop to see the full name). Then type
                    only your local number — no + or country code in the larger field. SMS rates may apply as per your
                    carrier.
                  </p>
                </div>
                <button type="submit" className="btn-accent w-full justify-center" disabled={busy}>
                  {busy ? 'Sending code…' : 'Text me a code'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="space-y-5">
                <div>
                  <label htmlFor="sms-code" className="block text-sm font-medium text-neutral-300 mb-2">
                    Enter the 6-digit code
                  </label>
                  <input
                    id="sms-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoComplete="one-time-code"
                    placeholder="••••••"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="block w-full px-4 py-3 rounded-md border border-neutral-600 bg-navy-900/80 text-white tracking-[0.35em] text-center text-lg font-mono focus:outline-none focus:ring-2 focus:ring-primary-500/60"
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button type="submit" className="btn-accent flex-1 justify-center" disabled={busy || code.length < 6}>
                    {busy ? 'Verifying…' : 'Sign in'}
                  </button>
                  <button
                    type="button"
                    className={`${CERTIFICATION_BTN_OUTLINE_DARK} flex-1 justify-center`}
                    disabled={busy}
                    onClick={() => {
                      setStep('phone')
                      setConfirmation(null)
                      setCode('')
                      setError('')
                    }}
                  >
                    Use a different number
                  </button>
                </div>
              </form>
            )}

            <div id="trp-phone-recaptcha" className="sr-only" aria-hidden />
          </motion.div>
        </div>
      </section>
    </div>
  )
}
